import React, { useState } from 'react';
import logo from '../img/logo-softmining.svg';
import Header from '../components/header';
import { processPdbId, processFile } from '../Utility/Matisse/matisseUtils';
import { retrieveMineData, consumeToken } from '../Utility/ConsumeToken/consumeToken';
import { FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const CheckPdbComponent = () => {
  const [pdbId, setPdbId] = useState('');
  const [multiPdbInput, setMultiPdbInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]); // SOLO PDB (es. "5MSA", "6G4Z", ...)
  const [mode, setMode] = useState('single'); // 'single' or 'file'
  const [showModal, setShowModal] = useState(false);

  // --- parse & validate .txt-like textarea (max 10 PDB IDs, uno per riga) ---
  const parseAndValidateTxt = (text) => {
    const linesRaw = text.split(/\r?\n/);
    const lines = linesRaw.map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return { ok: false, error: 'The input is empty.' };
    if (lines.length > 10) return { ok: false, error: 'You can insert at most 10 PDB IDs (one per line).' };

    const ids = [];
    for (let i = 0; i < lines.length; i++) {
      const tokens = lines[i].split(/\s+/).filter(Boolean);
      if (tokens.length !== 1) {
        return {
          ok: false,
          error: `Invalid format at line ${i + 1}: exactly one PDB ID per line is required.`,
        };
      }
      const id = tokens[0].toUpperCase();
      if (!/^[A-Z0-9]{4}$/.test(id)) {
        return {
          ok: false,
          error: `Invalid PDB ID at line ${i + 1}: "${tokens[0]}". Expected 4 alphanumeric characters.`,
        };
      }
      ids.push(id);
    }
    return { ok: true, ids };
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setResults([]); // reset elenco per nuova ricerca

    try {
      const apitoken = await retrieveMineData();
      if (apitoken.tokens > 3) {
        await consumeToken(-4);

        if (mode === 'single') {
          // singolo PDB ID
          const trimmedId = pdbId.trim().toUpperCase();
          await processPdbId(apitoken, trimmedId, setResults, setError);
        } else if (mode === 'file') {
          // modalità multipla: textarea con fino a 10 PDB ID
          if (!multiPdbInput.trim()) {
            throw new Error('Please insert at least one PDB ID.');
          }

          const res = parseAndValidateTxt(multiPdbInput);
          if (!res.ok) {
            throw new Error(res.error);
          }

          // Creiamo un "File" virtuale da passare a processFile,
          // riutilizzando tutta la logica già esistente lato utils/backend.
          const cleanTxt = res.ids.join('\n');
          const blob = new Blob([cleanTxt], { type: 'text/plain' });
          const virtualFile = new File([blob], 'pdb_ids.txt', { type: 'text/plain' });

          await processFile(virtualFile, apitoken, setResults, setError);
        }

        setShowModal(true);
      } else {
        alert('Insufficient token');
      }
    } catch (err) {
      const msg = err?.message || 'ITEM NOT FOUND';
      alert(msg);
      console.error(err);
      window.location.reload();
      return; // interrompe il flusso
    } finally {
      setLoading(false);
    }
  };

  const isSubmitDisabled =
    loading ||
    (mode === 'single' && !pdbId.trim()) ||
    (mode === 'file' && !multiPdbInput.trim());

  return (
    <>
      <Header />
      <div className="riga headerTools">
        <h3 className="titolo titoloFeature">Matisse</h3>
        <p className="text description">
          Matisse is a cutting-edge tool for analyzing the similarity between protein binding pockets. It enables researchers to compare structural features, predict cross-reactivity, and identify potential drug targets. By leveraging structural alignment techniques, Matisse aids in rational drug design and biomolecular research, offering a deeper understanding of ligand-binding properties.
        </p>
        <div className="space"></div>
      </div>
      <center>
        <div className="form formLogin formTools">
          <img src={logo} className="sign-logo" alt="logo" />

          {/* Mode Selection Buttons */}
          <label className="selectLabel">Choose how many PDB IDs to send:</label>
          <div className="mode-selection">
            <button
              type="button"
              className={`mode-button ${mode === 'single' ? 'active' : ''}`}
              onClick={() => setMode('single')}
            >
              One PDB ID
            </button>
            <button
              type="button"
              className={`mode-button ${mode === 'file' ? 'active' : ''}`}
              onClick={() => setMode('file')}
            >
              Multiple PDB IDs
            </button>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
          >
            {mode === 'single' && (
              <div>
                <label htmlFor="pdbId">PDB ID:</label>
                <input
                  placeholder="Enter PDB ID..."
                  type="text"
                  id="pdbId"
                  value={pdbId}
                  onChange={(e) => setPdbId(e.target.value)}
                  required
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
            )}

            {mode === 'file' && (
              <div>
                <label htmlFor="multiPdbInput">
                  Enter PDB IDs (one per line - max 10 lines):
                </label>
                <textarea
                  id="multiPdbInput"
                  rows={3}
                  placeholder={`e.g.\n1ABC\n2XYZ`}
                  value={multiPdbInput}
                  onChange={(e) => setMultiPdbInput(e.target.value)}
                  required
                  style={{ resize: 'vertical', textTransform: 'uppercase' }}
                />
              </div>
            )}

            <button className="sign-button button" type="submit" disabled={isSubmitDisabled}>
              {loading ? 'Processing...' : 'Submit'}
            </button>
          </form>

          <div className="riga">
            <Link className="textlink textLinkSign" to="/dashboard">
              Back to Dashboard{' '}
              <FaArrowRight size={12} style={{ marginLeft: '8px', marginBottom: '-1px' }} />
            </Link>
          </div>
        </div>

        {/* Results Modal */}
        {showModal && (
          <div className="popup-overlay">
            <div className="popup-content">
              <h4>Results:</h4>
              {error && (
                <p className="resultText" style={{ color: 'red' }}>
                  {error}
                </p>
              )}
              <ol>
                {results.map((pdb, index) => (
                  <label key={`${pdb}-${index}`}>
                    <li style={{ textAlign: 'left' }} className="resultCleanPDB">
                      {pdb}
                    </li>
                  </label>
                ))}
              </ol>
              <button className="sign-button button" onClick={() => setShowModal(false)}>
                Close
              </button>
              
            </div>
          </div>
        )}
        <div className="space"></div>
      </center>
    </>
  );
};

export default CheckPdbComponent;
